import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { hasPermission, isAdminLevel } from "../../api/auth";
import { CircleStackIcon } from "@heroicons/react/24/outline";
import { DynamicText } from "../common/DynamicText";
import { BellIcon } from "../notifications/BellIcon";
import { NotificationDropdown } from "../notifications/NotificationDropdown";
import { useNotificationPolling } from "../../hooks";
import {
  SupportChatWidget,
  BookDemoPage,
  DocsPage,
  HelpCenter,
  FloatingHelpButton,
} from "../support";
import { Footer } from "./Footer";

export const MainLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] =
    useState(false);
  const [lastLogin, setLastLogin] = useState("Loading...");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  // Support system states
  const [showBookDemo, setShowBookDemo] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);

  // Notification polling with adaptive intervals
  const { unreadCount, setUnreadCount } = useNotificationPolling({
    enabled: true,
    interval: 120000, // 2 minutes when active
    slowInterval: 300000, // 5 minutes when hidden or idle
  });

  useEffect(() => {
    // Handle click outside dropdown
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowUserDropdown(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node)
      ) {
        setShowNotificationDropdown(false);
      }
    };

    if (showUserDropdown || showNotificationDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserDropdown, showNotificationDropdown]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    // Set last login time
    const now = new Date();
    setLastLogin(now.toLocaleDateString());
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
      // Still navigate to login even if logout API fails
      navigate("/login", { replace: true });
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const getMenuItemClass = (active: boolean) => {
    const baseClass = `flex items-center rounded-lg transition-all duration-200 ${
      sidebarCollapsed ? "justify-center px-1 py-2" : "gap-3 px-4 py-3"
    }`;

    if (theme === "light") {
      return `${baseClass} ${
        active
          ? "bg-gradient-to-r from-sky-100 to-cyan-50 text-sky-600 font-medium border border-sky-200 shadow-sm"
          : "text-slate-600 hover:bg-sky-50/50 hover:text-sky-600"
      }`;
    }

    return `${baseClass} ${
      active
        ? "bg-gradient-to-r from-cyan-500/20 to-cyan-600/10 text-cyan-400 font-medium border border-cyan-500/30 shadow-lg shadow-cyan-500/10"
        : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
    }`;
  };

  const userInitials = user?.email?.substring(0, 2).toUpperCase() || "AD";

  return (
    <div className="flex h-screen overflow-hidden" data-theme={theme}>
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-30 transition-opacity"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 border-r flex flex-col transition-all duration-300 z-20 ${
          sidebarCollapsed ? "w-20" : "w-[200px]"
        } ${
          theme === "light"
            ? "bg-white border-sky-100 shadow-lg shadow-sky-100/50"
            : "bg-slate-900 border-slate-700/50 shadow-2xl"
        } md:translate-x-0 ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{
          top: "72px", // Below the header (h-[72px])
          height: "calc(100vh - 72px)",
        }}
      >
        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto custom-scrollbar pt-4">
          <div className="space-y-1 p-3">
            {/* Dashboard - Always visible */}
            <Link
              to="/dashboard"
              className={getMenuItemClass(isActive("/dashboard"))}
            >
              <svg
                className="w-5 h-5 text-cyan-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
              </svg>
              {!sidebarCollapsed && <span>Dashboard</span>}
            </Link>

            {/* Requisitions - Requires REQUISITIONS_READ */}
            {hasPermission(user, "REQUISITIONS_READ") && (
              <Link
                to="/requisitions"
                className={getMenuItemClass(
                  location.pathname.startsWith("/requisitions")
                )}
              >
                <svg
                  className="w-5 h-5 text-cyan-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                </svg>
                {!sidebarCollapsed && <span>Requisitions</span>}
              </Link>
            )}

            {/* Candidates - Requires CANDIDATES_READ */}
            {hasPermission(user, "CANDIDATES_READ") && (
              <Link
                to="/candidates"
                className={getMenuItemClass(
                  location.pathname.startsWith("/candidates")
                )}
              >
                <svg
                  className="w-5 h-5 text-cyan-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                </svg>
                {!sidebarCollapsed && <span>Candidates</span>}
              </Link>
            )}

            {/* Submissions - Requires SUBMISSIONS_READ */}
            {hasPermission(user, "SUBMISSIONS_READ") && (
              <Link
                to="/submissions"
                className={getMenuItemClass(
                  location.pathname.startsWith("/submissions")
                )}
              >
                <svg
                  className="w-5 h-5 text-cyan-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                </svg>
                {!sidebarCollapsed && <span>Submissions</span>}
              </Link>
            )}

            {/* Analytics - Customizable Dashboards */}
            {hasPermission(user, "ANALYTICS_READ") && (
              <Link
                to="/analytics"
                className={getMenuItemClass(
                  location.pathname.startsWith("/analytics")
                )}
              >
                <svg
                  className="w-5 h-5 text-cyan-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
                </svg>
                {!sidebarCollapsed && <span>Analytics</span>}
              </Link>
            )}

            {/* AI Automation & Performance Management - Restricted to Admin Level */}
            {isAdminLevel(user) && (
              <Link
                to="/automation"
                className={getMenuItemClass(
                  location.pathname.startsWith("/automation")
                )}
              >
                <svg
                  className="w-5 h-5 text-cyan-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                  <path
                    d="M9 12l2 2 4-4"
                    stroke="white"
                    strokeWidth="1.5"
                    fill="none"
                  />
                </svg>
                {!sidebarCollapsed && <span>AI Automation</span>}
              </Link>
            )}

            {/* DataHub - Requires DATA_MANAGEMENT_READ */}
            {hasPermission(user, "DATA_MANAGEMENT_READ") && (
              <Link
                to="/data-hub"
                className={getMenuItemClass(
                  location.pathname.startsWith("/data-hub")
                )}
              >
                <CircleStackIcon className="w-5 h-5 text-cyan-400" />
                {!sidebarCollapsed && <span>DataHub</span>}
              </Link>
            )}

            {/* Vendors - Requires admin level (hidden from recruiters until fully implemented) */}
            {isAdminLevel(user) && (
              <Link
                to="/vendors"
                className={getMenuItemClass(
                  location.pathname.startsWith("/vendors")
                )}
              >
                <svg
                  className="w-5 h-5 text-cyan-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 00-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z" />
                </svg>
                {!sidebarCollapsed && <span>Vendors</span>}
              </Link>
            )}

            {/* Tenants - Requires TENANTS_READ */}
            {hasPermission(user, "TENANTS_READ") && (
              <Link
                to="/tenants"
                className={getMenuItemClass(isActive("/tenants"))}
              >
                <svg
                  className="w-5 h-5 text-cyan-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
                </svg>
                {!sidebarCollapsed && <span>Tenants</span>}
              </Link>
            )}

            {/* Settings - Requires SETTINGS_READ */}
            {hasPermission(user, "SETTINGS_READ") && (
              <Link
                to="/settings"
                className={getMenuItemClass(
                  location.pathname.startsWith("/settings")
                )}
              >
                <svg
                  className="w-5 h-5 text-cyan-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                </svg>
                {!sidebarCollapsed && <span>Settings</span>}
              </Link>
            )}

            {/* Nexus - AI-Powered Insights */}
            <Link
              to="/ai-insights"
              className={getMenuItemClass(
                location.pathname.startsWith("/ai-insights")
              )}
            >
              <svg
                className="w-5 h-5 text-cyan-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                {/* Robot head */}
                <rect
                  x="7"
                  y="6"
                  width="10"
                  height="8"
                  rx="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* Antenna */}
                <line x1="12" y1="3" x2="12" y2="6" strokeLinecap="round" />
                <circle cx="12" cy="2.5" r="1" fill="currentColor" />
                {/* Eyes - intelligent look */}
                <circle cx="10" cy="9.5" r="1.2" fill="currentColor" />
                <circle cx="14" cy="9.5" r="1.2" fill="currentColor" />
                {/* Body */}
                <rect
                  x="9"
                  y="14"
                  width="6"
                  height="5"
                  rx="0.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* Arms - agent actions */}
                <path
                  d="M9 15L6 17M15 15L18 17"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {!sidebarCollapsed && <span>Nexus</span>}
            </Link>
          </div>
        </nav>

        {/* Collapse Toggle - Bottom of Sidebar (matches footer height) */}
        <div
          className={`py-3 px-3 border-t ${
            theme === "light" ? "border-sky-100" : "border-slate-700/50"
          }`}
        >
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`w-full flex items-center gap-3 px-3 py-1 rounded-lg transition-all duration-200 ${
              theme === "light"
                ? "hover:bg-sky-50 text-slate-500 hover:text-sky-600"
                : "hover:bg-slate-700/50 text-slate-400 hover:text-cyan-400"
            } ${sidebarCollapsed ? "justify-center" : ""}`}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {/* Jira-style sidebar collapse icon */}
            <svg
              className="w-5 h-5 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ transform: sidebarCollapsed ? "scaleX(-1)" : "none" }}
            >
              {/* Sidebar panel */}
              <rect
                x="3"
                y="3"
                width="18"
                height="18"
                rx="2"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Sidebar divider line */}
              <line
                x1="9"
                y1="3"
                x2="9"
                y2="21"
                strokeWidth="2"
                strokeLinecap="round"
              />
              {/* Collapse arrow */}
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 9l-3 3 3 3"
              />
            </svg>
            {!sidebarCollapsed && (
              <span className="text-sm font-medium">Collapse</span>
            )}
          </button>
        </div>
      </aside>

      {/* Top Header Bar */}
      <header
        className={`fixed top-0 h-[72px] flex items-stretch z-30 transition-all duration-300`}
        style={{ 
          left: 0, 
          right: 0,
          width: "100%",
          // Clean glassmorphism with gradient accent line
          background: theme === "light" 
            ? "rgba(255, 255, 255, 0.85)"
            : "rgba(15, 23, 42, 0.9)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: "none",
          boxShadow: theme === "light" 
            ? "0 1px 0 rgba(0, 0, 0, 0.05), 0 4px 20px -5px rgba(0, 0, 0, 0.05)"
            : "0 4px 30px -5px rgba(0, 0, 0, 0.4), 0 1px 0 rgba(255, 255, 255, 0.03)"
        }}
      >
        {/* Gradient accent line at bottom */}
        <div 
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, #06b6d4 0%, #8b5cf6 50%, #ec4899 100%)',
            opacity: theme === 'light' ? 0.2 : 0.3
          }}
        />
        {/* Left Section: Metatalio Logo - Matches sidebar width */}
        <div 
          className={`flex items-center justify-center border-r transition-all duration-300 shrink-0 ${
            theme === "light" ? "border-sky-100" : "border-slate-700/50"
          }`}
          style={{ width: sidebarCollapsed ? "80px" : "200px" }}
        >
          <Link to="/dashboard" className="flex items-center gap-3 group px-4">
            <img
              src="/logo.svg"
              alt="Metatalio Logo"
              className="w-9 h-9 shrink-0 group-hover:scale-105 transition-transform"
            />
            {!sidebarCollapsed && (
              <span
                className={`text-xl font-bold bg-clip-text text-transparent hidden sm:inline tracking-tight ${
                  theme === "light"
                    ? "bg-gradient-to-r from-sky-600 to-cyan-600"
                    : "bg-gradient-to-r from-cyan-400 to-purple-400"
                }`}
              >
                METATALIO
              </span>
            )}
          </Link>
        </div>

        {/* Right Section: Tenant + Search + Actions */}
        <div className="flex-1 flex items-center justify-between px-4 md:px-6">
          {/* Tenant Name */}
          {!user?.isSuperAdmin && user?.tenantName && (
            <div className="flex items-center gap-2">
              {user?.tenantLogo ? (
                <img
                  src={user.tenantLogo}
                  alt={user.tenantName || "Company Logo"}
                  className="w-7 h-7 rounded object-cover"
                />
              ) : (
                <div
                  className={`w-7 h-7 rounded flex items-center justify-center text-xs font-bold text-white ${
                    theme === "light"
                      ? "bg-gradient-to-br from-sky-500 to-cyan-600"
                      : "bg-gradient-to-br from-cyan-500 to-blue-600"
                  }`}
                >
                  {user?.tenantName?.substring(0, 2).toUpperCase() || "TN"}
                </div>
              )}
              <span
                className={`font-semibold text-base hidden md:inline ${
                  theme === "light" ? "text-slate-700" : "text-slate-200"
                }`}
              >
                {user?.tenantName}
              </span>
            </div>
          )}
          {user?.isSuperAdmin && <div />}

          {/* Center: Search Bar - Hidden on small mobile */}
          <div className="hidden sm:flex flex-1 max-w-xl mx-4">
            <div className="relative w-full">
              <svg
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                  theme === "light" ? "text-slate-400" : "text-slate-400"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search..."
                className={`w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none transition-all text-sm md:text-base ${
                  theme === "light"
                    ? "bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                    : "bg-slate-800 border border-slate-600 text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                }`}
              />
            </div>
          </div>

        {/* Right: Theme Toggle, Notification & User */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={`p-1.5 md:p-2 rounded-lg transition-colors border ${
              theme === "light"
                ? "bg-white hover:bg-slate-50 border-slate-200"
                : "bg-slate-900 hover:bg-slate-700 border-slate-600"
            }`}
          >
            {theme === "dark" ? (
              <svg
                className="w-6 h-6 text-yellow-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
              </svg>
            ) : (
              <svg
                className="w-6 h-6 text-slate-300"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  fillRule="evenodd"
                  d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>

          {/* Notification Bell - Hidden on mobile */}
          <div className="hidden sm:block relative" ref={notificationRef}>
            <BellIcon
              unreadCount={unreadCount}
              onClick={() =>
                setShowNotificationDropdown(!showNotificationDropdown)
              }
              isOpen={showNotificationDropdown}
            />
            <NotificationDropdown
              isOpen={showNotificationDropdown}
              onClose={() => setShowNotificationDropdown(false)}
              onUnreadCountChange={setUnreadCount}
            />
          </div>

          {/* User Profile */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                  theme === "light"
                    ? "bg-gradient-to-r from-sky-500 to-cyan-500 text-white"
                    : "bg-gradient-to-r from-cyan-400 to-purple-400 text-white"
                }`}
              >
                {userInitials}
              </div>
            </button>

            {showUserDropdown && (
              <div
                className={`absolute right-0 mt-2 w-56 rounded-lg border shadow-2xl overflow-hidden z-50 ${
                  theme === "light"
                    ? "bg-white border-slate-200"
                    : "bg-slate-800 border-slate-600"
                }`}
              >
                <div
                  className={`px-4 py-3 border-b ${
                    theme === "light"
                      ? "border-slate-200 bg-gradient-to-r from-sky-50 to-cyan-50"
                      : "border-slate-600 bg-slate-700/50"
                  }`}
                >
                  <div
                    className={`text-sm font-semibold ${
                      theme === "light" ? "text-slate-900" : "text-white"
                    }`}
                  >
                    {user?.email?.split("@")[0] || "User"}
                  </div>
                  <div
                    className={`text-xs ${
                      theme === "light" ? "text-slate-500" : "text-slate-400"
                    }`}
                  >
                    {user?.email || ""}
                  </div>
                </div>
                <Link
                  to="/profile"
                  className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                    theme === "light"
                      ? "text-slate-700 hover:bg-sky-50"
                      : "text-slate-200 hover:bg-slate-700"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <span>Profile</span>
                </Link>
                <Link
                  to="/support"
                  onClick={() => setShowUserDropdown(false)}
                  className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                    theme === "light"
                      ? "text-slate-700 hover:bg-sky-50"
                      : "text-slate-200 hover:bg-slate-700"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>Support</span>
                </Link>
                <div
                  className={`border-t ${
                    theme === "light" ? "border-slate-200" : "border-slate-600"
                  }`}
                ></div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLogout();
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-red-500 transition-colors text-left ${
                    theme === "light" ? "hover:bg-red-50" : "hover:bg-slate-700"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      </header>

      {/* Main Content Area with Footer */}
      <div
        className="flex flex-col transition-all duration-300"
        style={{
          marginLeft:
            window.innerWidth >= 768
              ? sidebarCollapsed
                ? "80px"
                : "200px"
              : "0",
          width:
            window.innerWidth >= 768
              ? `calc(100vw - ${sidebarCollapsed ? "80px" : "200px"})`
              : "100vw",
          height: "100vh",
          paddingTop: "72px", // Account for fixed header (h-[72px])
        }}
      >
        {/* Main Content */}
        <main
          className={`flex-1 pt-4 p-4 md:pt-4 md:p-8 overflow-auto ${
            theme === "light" ? "bg-slate-50 text-slate-900" : "text-slate-100"
          }`}
          style={{
            background:
              theme === "light"
                ? "#F8FAFC"
                : "linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%)",
          }}
        >
          {children}
        </main>

        {/* Footer */}
        <Footer />
      </div>

      {/* Floating Help Button - shows only on Support page */}
      {location.pathname === "/support" && <FloatingHelpButton />}

      {/* Only show Support Chat Widget on Support page */}
      {location.pathname === "/support" && (
        <SupportChatWidget
          onBookDemo={() => setShowBookDemo(true)}
          onOpenDocs={() => setShowDocs(true)}
          onOpenHelp={() => setShowHelpCenter(true)}
        />
      )}

      {/* Support Modals */}
      {showBookDemo && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-7xl w-full h-[90vh] overflow-hidden">
            <BookDemoPage />
            <button
              onClick={() => setShowBookDemo(false)}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {showDocs && <DocsPage onClose={() => setShowDocs(false)} />}

      {showHelpCenter && (
        <HelpCenter onClose={() => setShowHelpCenter(false)} />
      )}
    </div>
  );
};
